// Demo constants — no React dependency, safe to import from workers and UI alike

export const DEMO_IDS = [
  'insurance-renewal',
  'financial-services-fees',
  'healthcare-reminder',
  'saas-churn',
  'preference-center-research',
] as const;

export type DemoId = (typeof DEMO_IDS)[number];

// ---------------------------------------------------------------------------
// Approved headlines
// ---------------------------------------------------------------------------

export const DEMO_APPROVED_HEADLINES: Record<DemoId, string> = {
  'insurance-renewal':
    "Message B likely wins (medium-high confidence) — but 'market conditions' phrasing triggered NAIC complaint pattern",
  'financial-services-fees':
    'Message B wins overall — but Message A fails CFPB plain-language threshold for sub-college-educated segment',
  'healthcare-reminder':
    'Message C wins overall — but Message A fails plain-language test for low-literacy members',
  'saas-churn': 'Your churn survey is overstating price by 23 points',
  'preference-center-research':
    'Approach B reduces opt-outs 6 points across the panel — but Approach A wins on the high-engagement segment',
};

// ---------------------------------------------------------------------------
// Industry slug -> demo ID mapping (both underscore and hyphen variants)
// ---------------------------------------------------------------------------

export const INDUSTRY_TO_DEMO_ID: Record<string, DemoId> = {
  insurance: 'insurance-renewal',
  'financial-services': 'financial-services-fees',
  financial_services: 'financial-services-fees',
  healthcare: 'healthcare-reminder',
  saas: 'saas-churn',
};

// ---------------------------------------------------------------------------
// Aha card types and shape
// ---------------------------------------------------------------------------

export type AhaCardType =
  | 'compliance-check-card'
  | 'plain-language-gauge'
  | 'plain-language-rewrite-card'
  | 'attribution-gap-card';

export type AhaSeverity = 'high' | 'medium' | 'low';

export interface DemoAhaCard {
  card_id: string;
  type: AhaCardType;
  title: string;
  body: string;
  severity: AhaSeverity;
}

// ---------------------------------------------------------------------------
// Aha cards per demo (exactly 4 per demo)
// ---------------------------------------------------------------------------

export const DEMO_AHA_CARDS: Record<DemoId, DemoAhaCard[]> = {
  'insurance-renewal': [
    {
      card_id: 'insurance-renewal-compliance-naic',
      type: 'compliance-check-card',
      title: 'NAIC complaint pattern detected',
      body:
        "The phrase 'market conditions' in Message B matches language flagged in 14% of NAIC policyholder complaints filed between 2021 and 2023. Regulators have cited vague causation language as a driver of consumer confusion during renewal disputes. Consider replacing with a specific rate-driver disclosure.",
      severity: 'high',
    },
    {
      card_id: 'insurance-renewal-attribution-trust-price',
      type: 'attribution-gap-card',
      title: 'Trust and price signals are not independent',
      body:
        'Respondents who rate trust highly in Message B also report lower perceived price — even when the quoted premium is identical across messages. This correlation suggests trust language is partially suppressing price sensitivity. Attribution of churn risk to price alone will overstate the problem in this segment.',
      severity: 'medium',
    },
    {
      card_id: 'insurance-renewal-plain-language-claimants',
      type: 'plain-language-gauge',
      title: 'Recent claimants under-served by both messages',
      body:
        'Policyholders who filed a claim in the prior 12 months score both messages 18 points lower on comprehension than non-claimants. The Flesch-Kincaid grade level of the renewal letter (grade 13.2) exceeds the recommended threshold for post-claim communications by three grade levels. This segment is at elevated lapse risk.',
      severity: 'high',
    },
    {
      card_id: 'insurance-renewal-rewrite-loyalty',
      type: 'plain-language-rewrite-card',
      title: 'Loyalty language backfires post-claim',
      body:
        "The phrase 'thank you for your continued loyalty' scores negatively among respondents who had a claim partially denied. Among that group, the phrase is associated with a 12-point drop in intent to renew compared to a neutral acknowledgment. A plain rewrite removing the loyalty framing recovers most of the gap.",
      severity: 'medium',
    },
  ],

  'financial-services-fees': [
    {
      card_id: 'fin-fees-plain-language-cfpb',
      type: 'plain-language-gauge',
      title: 'CFPB plain-language threshold not met for key segment',
      body:
        'Message A reads at grade 14.7 on the Flesch-Kincaid scale. CFPB guidance on fee disclosures recommends a maximum of grade 8 for materials targeting the general public. Among respondents without a college degree, comprehension of the fee structure drops to 41%, well below the 70% threshold considered acceptable for informed consent.',
      severity: 'high',
    },
    {
      card_id: 'fin-fees-rewrite-available',
      type: 'plain-language-rewrite-card',
      title: 'Plain-language rewrite available for Message A',
      body:
        'A restructured version of Message A — breaking fee disclosures into a three-row table with labeled columns — raises comprehension scores by 22 points among non-college-educated respondents without changing the substantive disclosures. The rewrite keeps all required regulatory language intact.',
      severity: 'medium',
    },
    {
      card_id: 'fin-fees-attribution-trust-gap',
      type: 'attribution-gap-card',
      title: 'Trust gap between college-educated and non-college-educated segments',
      body:
        'Message B earns a 61-point trust score among college-educated respondents but only a 39-point trust score among non-college-educated respondents — a 22-point gap. Because these segments differ in product penetration, aggregating trust scores masks the risk that fee disclosure is driving churn disproportionately in lower-education households.',
      severity: 'high',
    },
    {
      card_id: 'fin-fees-compliance-disclosure',
      type: 'compliance-check-card',
      title: 'Fee disclosure completeness check',
      body:
        'Message A omits the account maintenance fee waiver condition, which is required to be disclosed in plain language under Regulation E. The omission does not appear in Message B. If Message A is deployed at scale, the missing disclosure creates remediation exposure across the affected account population.',
      severity: 'medium',
    },
  ],

  'healthcare-reminder': [
    {
      card_id: 'health-reminder-plain-language-literacy',
      type: 'plain-language-gauge',
      title: 'Low-literacy members at risk of no-show',
      body:
        'Message A reads at grade 11.4. Research from the Health Literacy Tool Shed benchmarks appointment reminder comprehension drop-off beginning around grade 7 for members with low health literacy. In this sample, low-literacy respondents report a 31% intent-to-attend rate for Message A versus 68% for Message C — a gap that compounds across chronic care populations.',
      severity: 'high',
    },
    {
      card_id: 'health-reminder-rewrite',
      type: 'plain-language-rewrite-card',
      title: 'Plain-language rewrite closes the show-rate gap',
      body:
        'A rewrite of Message A using active voice, sub-grade-7 vocabulary, and a single bolded action item (date, time, location) raises intent-to-attend scores among low-literacy respondents by 27 points. The rewrite does not reduce comprehension or trust among high-literacy respondents, making it safe to deploy as the default.',
      severity: 'high',
    },
    {
      card_id: 'health-reminder-attribution-channel',
      type: 'attribution-gap-card',
      title: 'Channel attribution gap in show-rate data',
      body:
        'Respondents who report receiving both SMS and postal reminders attribute their attendance decision to SMS at a 4-to-1 ratio — yet postal volume accounts for 60% of reminder spend. If show-rate improvement is attributed to the last-touch channel in the analytics system, postal will be over-credited and SMS investment will appear under-performing.',
      severity: 'medium',
    },
    {
      card_id: 'health-reminder-compliance-hipaa',
      type: 'compliance-check-card',
      title: 'HIPAA safe harbor fields present in Message B',
      body:
        'Message B includes the appointment specialty type alongside the member name and date. Under the HIPAA minimum necessary standard and the safe harbor de-identification method, combining name, date, and specialty in a single communication may constitute a disclosure of protected health information beyond what is required for the reminder function. Legal review is recommended before deployment.',
      severity: 'high',
    },
  ],

  'preference-center-research': [
    {
      card_id: 'pref-center-twin-panel-headline',
      type: 'attribution-gap-card',
      title: 'High-engagement segment reverses the panel-wide result',
      body:
        'Approach B reduces opt-out intent by 6 points across all 1,200 twins. But in the high-engagement under-35 segment (n=187 validated twins), Approach A outperforms Approach B by 9 points on category granularity preference. A single panel-wide rollout of Approach B would leave the highest-value segment with a suboptimal experience.',
      severity: 'high',
    },
    {
      card_id: 'pref-center-behavioral-framing',
      type: 'attribution-gap-card',
      title: 'Approach C (behavioral) underperforms stated preference',
      body:
        'Approach C (4 behavioral categories) was preferred in stated-choice by 38% of respondents — the highest of the three. But twin panel bulk queries predict Approach C produces the highest opt-out intent (31%) when customers are exposed to it in context. Stated preference and predicted behavior diverge by 14 points for this approach.',
      severity: 'high',
    },
    {
      card_id: 'pref-center-confidence-floor',
      type: 'plain-language-gauge',
      title: '73 twins below confidence floor — recent joiners',
      body:
        '73 twins (6.1% of the panel) fell below the Wilson lower-bound confidence floor of 0.4. All 73 are customers who joined in the last 90 days and had fewer than 5 holdout questions in the prior survey wave. Their reactions to preference-center changes are not represented in this result. If recent joiners are a strategically important cohort, run a targeted validation wave.',
      severity: 'medium',
    },
    {
      card_id: 'pref-center-ensemble-dissent',
      type: 'compliance-check-card',
      title: 'Claims segment shows high ensemble dissent',
      body:
        'For the recent-claimant segment (n=214), ensemble dissent on category framing preference is 0.41 — the highest of any segment. This means the 8 reasoning passes disagree significantly on which approach this segment prefers. The result for this segment should be treated as uncertain and routed to human validation before any decision is made about their experience.',
      severity: 'medium',
    },
  ],

  'saas-churn': [
    {
      card_id: 'saas-churn-attribution-price-overstatement',
      type: 'attribution-gap-card',
      title: 'Price overstated by 23 percentage points',
      body:
        "When respondents who selected 'too expensive' are re-surveyed with a conjoint-style follow-up, only 31% maintain price as their primary reason for cancellation. The remaining 69% reveal product fit or activation issues as the primary driver. Your current survey design is inflating price attribution by approximately 23 percentage points relative to the conjoint baseline.",
      severity: 'high',
    },
    {
      card_id: 'saas-churn-attribution-activation',
      type: 'attribution-gap-card',
      title: 'Activation failure masked as price sensitivity',
      body:
        "Among churned accounts that cited price, 58% never completed the core activation workflow (defined as completing three key actions within the first 14 days). This pattern suggests that price is being selected as a post-hoc rationalization rather than a root cause. Accounts that completed activation but still churned show a price attribution rate of only 19%, close to the population baseline.",
      severity: 'high',
    },
    {
      card_id: 'saas-churn-plain-language-nps',
      type: 'plain-language-gauge',
      title: 'NPS satisficing detected in exit survey',
      body:
        'The NPS question in the exit survey uses a 0-10 scale with no labeled midpoints. Response time data shows a median of 1.8 seconds for NPS responses, compared to 6.4 seconds for open-text fields — a pattern consistent with satisficing (selecting a convenient answer rather than a considered one). NPS scores from this survey should be treated as low-fidelity signals until the question design is revised.',
      severity: 'medium',
    },
    {
      card_id: 'saas-churn-compliance-cohort',
      type: 'compliance-check-card',
      title: 'Cohort contamination detected',
      body:
        'The survey sample mixes accounts on the legacy pricing plan with accounts on the current pricing plan in a single cohort. Because legacy plan accounts pay materially different rates, price sensitivity scores are not comparable across the two groups. Reporting a single price-as-churn-driver figure for this mixed cohort will produce misleading results for any pricing or packaging decision downstream.',
      severity: 'high',
    },
  ],
};

// ---------------------------------------------------------------------------
// DemoConfig — top-level config object consumed by the demo runner and UI
// ---------------------------------------------------------------------------

export interface DemoConfig {
  demoId: DemoId;
  industrySlug: string;
  title: string;
  preReadQuestion: string;
  approvedHeadline: string;
  ahaCards: DemoAhaCard[];
}

export const DEMO_CONFIGS: Record<DemoId, DemoConfig> = {
  'insurance-renewal': {
    demoId: 'insurance-renewal',
    industrySlug: 'insurance',
    title: 'Insurance Renewal Message Test',
    preReadQuestion:
      'Which of these renewal messages would make you most likely to stay with your insurer?',
    approvedHeadline: DEMO_APPROVED_HEADLINES['insurance-renewal'],
    ahaCards: DEMO_AHA_CARDS['insurance-renewal'],
  },
  'financial-services-fees': {
    demoId: 'financial-services-fees',
    industrySlug: 'financial-services',
    title: 'Fee Disclosure Message Test',
    preReadQuestion:
      'Which fee disclosure message do you find clearest and most trustworthy?',
    approvedHeadline: DEMO_APPROVED_HEADLINES['financial-services-fees'],
    ahaCards: DEMO_AHA_CARDS['financial-services-fees'],
  },
  'healthcare-reminder': {
    demoId: 'healthcare-reminder',
    industrySlug: 'healthcare',
    title: 'Appointment Reminder Effectiveness',
    preReadQuestion:
      'Which appointment reminder would most reliably prompt you to keep your appointment?',
    approvedHeadline: DEMO_APPROVED_HEADLINES['healthcare-reminder'],
    ahaCards: DEMO_AHA_CARDS['healthcare-reminder'],
  },
  'saas-churn': {
    demoId: 'saas-churn',
    industrySlug: 'saas',
    title: 'Churn Survey Validation',
    preReadQuestion:
      'When you cancel a software subscription, which of these best describes your primary reason?',
    approvedHeadline: DEMO_APPROVED_HEADLINES['saas-churn'],
    ahaCards: DEMO_AHA_CARDS['saas-churn'],
  },
  'preference-center-research': {
    demoId: 'preference-center-research',
    industrySlug: 'financial-services',
    title: 'Preference-Center Category Research — Twin Panel Demo',
    preReadQuestion:
      'Which category framing approach do you prefer for managing your communication preferences?',
    approvedHeadline: DEMO_APPROVED_HEADLINES['preference-center-research'],
    ahaCards: DEMO_AHA_CARDS['preference-center-research'],
  },
};
